import { tokens } from '@lmc/design-system/tokens'

/** The HTML around a captured chart SVG: the DS legend and a resident chart's subtitle. */
export interface ChartPdfExtras {
  legend: { colour: string | null; label: string }[]
  subtitle: string | null
}

const SWATCH_SIZE = 8

/** pdfmake only understands hex and named colours, so anything else drops the swatch. */
const pdfColour = (value: string | null) =>
  value && /^(#[0-9a-f]{3,8}|[a-z]+)$/i.test(value) ? value : null

/**
 * Reads the legend items of a DS chart (`[data-slot="chart-legend"]`) and the
 * `[data-chart-subtitle]` line above it. Series toggled off in the legend are omitted.
 */
export const readChartPdfExtras = (
  chartRoot: Element | null,
  subtitleElement: Element | null
): ChartPdfExtras => {
  const items = chartRoot
    ? Array.from(
        chartRoot.querySelectorAll(
          '[data-slot="chart-legend"] > button, [data-slot="chart-legend"] > div'
        )
      )
    : []

  const legend = items
    .filter((item) => item.getAttribute('aria-pressed') !== 'false')
    .map((item) => {
      const swatch = item.querySelector('svg rect, svg line')
      const label = Array.from(item.querySelectorAll(':scope > span'))
        .map((span) => span.textContent?.trim())
        .filter(Boolean)
        .join(' ')
      return {
        colour: pdfColour(
          swatch?.getAttribute('fill') ?? swatch?.getAttribute('stroke') ?? null
        ),
        label
      }
    })
    .filter(({ label }) => label)

  return {
    legend,
    subtitle: subtitleElement?.textContent?.trim() || null
  }
}

/** pdfmake content for the subtitle, drawn above the chart image. */
export const chartSubtitleContent = (subtitle: string | null) =>
  subtitle
    ? {
        alignment: 'center',
        color: tokens.color.text.secondary,
        fontSize: 9,
        margin: [0, 10, 0, 0],
        text: subtitle
      }
    : null

/** pdfmake content for a centred legend row of swatches and labels, drawn under the chart image. */
export const chartLegendContent = (legend: ChartPdfExtras['legend']) =>
  legend.length
    ? {
        columnGap: 12,
        columns: [
          { text: '', width: '*' },
          ...legend.map(({ colour, label }) => ({
            columnGap: 4,
            columns: [
              colour
                ? {
                    canvas: [
                      {
                        color: colour,
                        h: SWATCH_SIZE,
                        r: 2,
                        type: 'rect',
                        w: SWATCH_SIZE,
                        x: 0,
                        y: 1
                      }
                    ],
                    width: SWATCH_SIZE
                  }
                : null,
              { fontSize: 9, text: label, width: 'auto' }
            ].filter(Boolean),
            width: 'auto'
          })),
          { text: '', width: '*' }
        ],
        margin: [0, 0, 0, 30]
      }
    : null
